import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/new-components/ui/button';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { LANDING_NAV_ITEMS } from '../components/Layout/navConfig';
import { BRAND_ASSETS, TENANT } from '../config';
import api from '../services/api';

/* ─── Override types ──────────────────────────────────────────── */
interface AboutOverrides {
  heroHeading?: string;
  heroBody?: string;
  heroCtaText?: string;
  heroImage?: string;
  empoweringHeading?: string;
  empoweringStatements?: Array<{ title: string; description: string }>;
  strategyHeading?: string;
  strategyBody?: string;
  strategyImage?: string;
  featureCards?: Array<{ title: string; description: string }>;
  newsletterHeading?: string;
  newsletterSubheading?: string;
  footerDisclaimer?: string;
}

function parseAboutContent(content: any): AboutOverrides {
  const overrides: AboutOverrides = {};
  const sections = content?.settings?.sections;
  if (!sections) return overrides;

  const h = sections.hero || {};
  if (h.heading) overrides.heroHeading = h.heading;
  if (h.body) overrides.heroBody = h.body;
  if (h.ctaText) overrides.heroCtaText = h.ctaText;
  if (h.image) overrides.heroImage = h.image;

  const e = sections.empowering || {};
  if (e.heading) overrides.empoweringHeading = e.heading;
  if (Array.isArray(e.statements) && e.statements.length) overrides.empoweringStatements = e.statements;

  const s = sections.strategy || {};
  if (s.heading) overrides.strategyHeading = s.heading;
  if (s.body) overrides.strategyBody = s.body;
  if (s.image) overrides.strategyImage = s.image;

  const f = sections.features || {};
  if (Array.isArray(f.items) && f.items.length) overrides.featureCards = f.items;

  const nl = sections.newsletter || {};
  if (nl.heading) overrides.newsletterHeading = nl.heading;
  if (nl.subheading) overrides.newsletterSubheading = nl.subheading;

  const ft = sections.footer || {};
  if (ft.disclaimer) overrides.footerDisclaimer = ft.disclaimer;

  return overrides;
}

const About: React.FC = () => {
  const [overrides, setOverrides] = useState<AboutOverrides>({});

  useEffect(() => {
    api.get('/site-pages/about')
      .then((res: any) => {
        const data = res?.data?.data || res?.data || res;
        if (data?.content) {
          setOverrides(parseAboutContent(data.content));
        }
      })
      .catch(() => { /* no published about page — use hardcoded defaults */ });
  }, []);

  const featureCards = overrides.featureCards || [
    { title: 'Simplified Access', description: 'Invest in curated real estate deals—professionally vetted and managed' },
    { title: 'Passive', description: 'We handle it all - from finances to repairs and everything in between' },
    { title: 'Earn & Grow', description: 'Quarterly cashflow from rental income plus property appreciation' },
    { title: 'Diversify', description: 'Own shares in multiple properties to spread risk and maximize returns' },
  ];

  const empoweringStatements = overrides.empoweringStatements || [
    { title: 'Accessible', description: 'Our mission is to make high-quality real estate investing simple and accessible' },
    { title: 'Transparent', description: 'We believe in transparency, performance, and shared opportunity, not complexity or exclusivity.' },
    { title: 'Consistent', description: "Our goal is simple: help investors earn consistent, tax-efficient returns through properties they're proud to own." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader navItems={LANDING_NAV_ITEMS} />

      {/* Hero Section - DARK with building image */}
      <section className="relative bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))]">
        <div className="container mx-auto px-4 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left: Copy */}
            <div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-medium mb-8 leading-tight">
                {overrides.heroHeading || <>About{' '}<span className="italic font-serif">{TENANT.branding.name}</span></>}
              </h1>

              <div className="space-y-5 text-gray-300 text-lg leading-relaxed max-w-xl">
                <p>
                  Real estate has long been a powerful way to build wealth but getting started
                  isn't easy. Navigating this sector often means facing high capital requirements,
                  changing regulations, complex deal structures, and the ongoing demands of
                  property management.
                </p>
                <p>
                  <strong className="text-white">{TENANT.branding.name}</strong> is changing that.
                </p>
                <p>
                  Our team evaluates hundreds of deals to bring you{' '}
                  <strong className="text-white">curated opportunities</strong> in
                  high-performing rental homes, vacation properties, and multifamily real estate.
                  Each asset is professionally optimized and fully managed. Making this a truly{' '}
                  <strong className="text-white">hands-off investment experience</strong>.
                </p>
                <p>This is real estate investing, simplified.</p>
                <p className="font-semibold text-white">Join The Hive</p>
              </div>

              <div className="mt-8">
                <Link to="/register">
                  <Button
                    size="lg"
                    className="text-base font-bold px-10 py-6 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-wide"
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Building image */}
            <div className="hidden lg:block">
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={overrides.heroImage || "https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=800&q=80"}
                  alt="Modern building architecture"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Empowering Investors - WHITE */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 md:gap-20 items-center max-w-6xl mx-auto">
            {/* Left: Heading */}
            <div>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-serif font-medium text-foreground leading-tight">
                {overrides.empoweringHeading || <>Empowering<br />investors</>}
              </h2>
            </div>

            {/* Right: Mission statements */}
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              {empoweringStatements.map((s, i) => (
                <p key={i}>{s.description || s.title}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strategy Section - DARK with honey image */}
      <section className="py-20 md:py-28 bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))]">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left: Honey dipper image */}
            <div className="flex justify-center">
              <img
                src={overrides.strategyImage || "https://images.unsplash.com/photo-1587049352846-4a222e784d38?auto=format&fit=crop&w=600&q=80"}
                alt="Honey dripping"
                className="w-auto max-h-[450px] object-contain drop-shadow-2xl"
              />
            </div>

            {/* Right: Strategy copy */}
            <div className="text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 leading-tight">
                {overrides.strategyHeading || 'We target properties with exceptional potential and long-term value.'}
              </h2>
              <p className="text-gray-400 text-base md:text-lg leading-relaxed">
                {overrides.strategyBody || 'Our strategy focuses on acquiring high-performing rental homes in desirable, high-demand markets. Every property is hand-selected through a data-driven process that evaluates cash flow, appreciation potential, and regulatory stability. Once acquired, each asset is professionally managed to deliver consistent income, strong appreciation, and a truly passive ownership experience'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards - DARK */}
      <section className="pb-20 md:pb-28 bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))]">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {featureCards.map((card) => (
              <div
                key={card.title}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors"
              >
                <h3 className="text-lg font-bold text-white mb-3">{card.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our List - PRIMARY/YELLOW */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
            {/* Left: Email list */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-serif font-medium mb-4">
                {overrides.newsletterHeading || 'Join our List'}
              </h2>
              <p className="text-sm leading-relaxed mb-6 max-w-md opacity-90">
                {overrides.newsletterSubheading || 'Add your email below to receive updates from us, including our guides to investing and notifications about new rental properties & markets.'}
              </p>
              <form
                className="flex gap-2 max-w-md"
                onSubmit={(e) => {
                  e.preventDefault();
                  window.location.href = '/register';
                }}
              >
                <input
                  type="email"
                  placeholder="Email Address"
                  className="flex-1 px-4 py-3 rounded-md border border-black/20 bg-white text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/20"
                />
                <Button
                  type="submit"
                  className="bg-black text-white hover:bg-black/80 font-semibold px-6"
                >
                  SIGN UP
                </Button>
              </form>
            </div>

            {/* Right: Footer links */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
              <div>
                <h4 className="font-bold text-sm mb-3">Invest</h4>
                <ul className="space-y-2 text-sm opacity-80">
                  <li>
                    <Link to="/register" className="hover:opacity-100 transition-opacity">
                      Sign up
                    </Link>
                  </li>
                  <li>
                    <Link to="/invest" className="hover:opacity-100 transition-opacity">
                      Browse properties
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-sm mb-3">Learn</h4>
                <ul className="space-y-2 text-sm opacity-80">
                  <li>
                    <Link to="/hive-fund" className="hover:opacity-100 transition-opacity">
                      What is a Fund
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-sm mb-3">Company</h4>
                <ul className="space-y-2 text-sm opacity-80">
                  <li>
                    <Link to="/about" className="hover:opacity-100 transition-opacity">
                      About
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="hover:opacity-100 transition-opacity">
                      Contact Us
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-sm mb-3">Follow Us</h4>
                <ul className="space-y-2 text-sm opacity-80">
                  <li>
                    <a
                      href="https://x.com/honeyshares"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:opacity-100 transition-opacity flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                      X
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://instagram.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:opacity-100 transition-opacity flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                      </svg>
                      Instagram
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:opacity-100 transition-opacity flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                      Facebook
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Footer - DARK */}
      <footer className="bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))] py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img
                src={BRAND_ASSETS.logo.allWhite}
                alt={TENANT.branding.name}
                className="h-6 w-auto"
              />
              <span className="text-sm text-gray-400">
                © 2026 {TENANT.branding.name}. All rights reserved.
              </span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/terms" className="hover:text-primary transition-colors">
                Terms of Service
              </Link>
              <Link to="/privacy" className="hover:text-primary transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center mt-6 max-w-4xl mx-auto">
            {overrides.footerDisclaimer || 'Investing in real estate involves risk, including loss of principal. Past performance does not guarantee future results. Securities offered through registered broker-dealer. This is not an offer to sell securities.'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default About;
